import { useEffect } from "react";

/**
 * Sets per-route document.title, meta description, and OG/Twitter tags.
 * Cleans up by leaving the last-set values in place (fine for SPA).
 */
export default function useSEO({ title, description, image, path, noindex = false }) {
  useEffect(() => {
    if (title) document.title = title;

    const ensure = (selector, create) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      return el;
    };

    const setMeta = (name, content) => {
      if (!content) return;
      const el = ensure(`meta[name="${name}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute("name", name);
        return m;
      });
      el.setAttribute("content", content);
    };

    const setProp = (property, content) => {
      if (!content) return;
      const el = ensure(`meta[property="${property}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute("property", property);
        return m;
      });
      el.setAttribute("content", content);
    };

    const setLink = (rel, href) => {
      if (!href) return;
      const el = ensure(`link[rel="${rel}"]`, () => {
        const l = document.createElement("link");
        l.setAttribute("rel", rel);
        return l;
      });
      el.setAttribute("href", href);
    };

    const url = path ? `https://instantsiterevival.com${path}` : window.location.href;

    setMeta("description", description);
    setMeta("robots", noindex ? "noindex,nofollow" : "index,follow");
    setProp("og:type", "website");
    setProp("og:site_name", "Instant Site Revival");
    setProp("og:title", title);
    setProp("og:description", description);
    setProp("og:url", url);
    setProp("og:image", image || "https://instantsiterevival.com/og-default.png");
    setProp("og:image:width", "1200");
    setProp("og:image:height", "630");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image || "https://instantsiterevival.com/og-default.png");
    setLink("canonical", url);
  }, [title, description, image, path, noindex]);
}
